import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Link,
  Stack,
  Divider,
  useTheme,
  Fade,
  Alert,
  CircularProgress,
  alpha,
  InputAdornment,
  TextField,
  IconButton,
} from '@mui/material';
import {
  LocalHospital,
  MedicalServices,
  PersonOutline,
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import VirtualPATLogo from '../components/virtualpat-theme/VirtualPATLogo';
import { useResponsive } from '../theme/responsive';
import { useEnterpriseAuth } from '../context/EnterpriseAuthContext';
import { useSnackbar } from '../components/SnackbarProvider';
import OAuthProviderList from '../components/OAuthProviderList';
import SAMLLoginButton from '../components/SAMLLoginButton';
import { setEnvironmentMode, getConfig } from '../config/environmentConfig';
import { isFeatureEnabled } from '../utils/featureFlags';

const ModernTextField = React.forwardRef(({ icon, theme, ...props }, ref) => (
  <TextField
    {...props}
    ref={ref}
    fullWidth
    variant="outlined"
    InputProps={{
      startAdornment: icon && (
        <InputAdornment position="start">
          {React.cloneElement(icon, { sx: { color: 'text.secondary', fontSize: 20 } })}
        </InputAdornment>
      ),
      ...props.InputProps,
      sx: {
        borderRadius: 2,
        bgcolor: 'background.paper',
        '&:hover': { bgcolor: theme ? alpha(theme.palette.primary.main, 0.02) : undefined },
        ...props.InputProps?.sx,
      },
    }}
    sx={{
      '& .MuiOutlinedInput-root': {
        '& fieldset': { borderColor: theme ? alpha(theme.palette.divider, 0.8) : undefined },
        '&:hover fieldset': { borderColor: theme?.palette.primary.light },
        '&.Mui-focused fieldset': { borderWidth: 2 },
      },
      ...props.sx,
    }}
  />
));

function LoginTab({ icon, label, active, onClick }) {
  const theme = useTheme();
  
  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      sx={{
        flex: 1,
        py: { xs: 1, sm: 1.5 },
        px: { xs: 1, sm: 2 },
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: 0.5, sm: 1 },
        cursor: 'pointer',
        borderRadius: 2,
        bgcolor: active ? alpha('#fff', 0.2) : alpha('#fff', 0.05),
        color: 'white',
        transition: 'all 0.2s',
        border: '1px solid',
        borderColor: active ? alpha('#fff', 0.3) : 'transparent',
        minHeight: 44,
        '&:hover': {
          bgcolor: alpha('#fff', 0.15),
          borderColor: alpha('#fff', 0.2),
        },
      }}
    >
      {React.cloneElement(icon, { sx: { fontSize: { xs: 18, sm: 20 } } })}
      <Typography 
        variant="body2" 
        fontWeight={active ? 700 : 500}
        sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export default function LoginPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useResponsive();
  const { showSnackbar } = useSnackbar();
  const { login, isLoading: authLoading, error: authError, clearError } = useEnterpriseAuth();
  
  const [userType, setUserType] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');
  
  const loginUXV2 = isFeatureEnabled('LOGIN_UX_V2');

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlMode = urlParams.get('mode');
    const stateMode = location.state?.mode;
    
    let initialUserType = null;
    
    if (urlMode) {
      if (urlMode === 'patient') initialUserType = 'patient';
      else if (urlMode === 'asc') initialUserType = 'asc';
      else if (urlMode === 'hospital' || urlMode === 'staff') initialUserType = 'hospital';
      localStorage.setItem('appMode', urlMode === 'staff' ? 'hospital' : urlMode);
    } else if (stateMode) {
      if (stateMode === 'patient') initialUserType = 'patient';
      else if (stateMode === 'asc') initialUserType = 'asc';
      else if (stateMode === 'hospital' || stateMode === 'staff') initialUserType = 'hospital';
      localStorage.setItem('appMode', stateMode === 'staff' ? 'hospital' : stateMode);
    }
    
    setUserType(initialUserType);
  }, [location.search, location.state]);

  useEffect(() => {
    if (location.state?.message) {
      if (loginUXV2) showSnackbar(location.state.message, 'info');
      else setMessage(location.state.message);
    }
    if (localStorage.getItem('logoutReason')) {
      const reason = localStorage.getItem('logoutReason');
      setMessage(reason);
      localStorage.removeItem('logoutReason');
    }
    if (authError) {
      if (loginUXV2) showSnackbar(authError, 'error');
      else setErrors({ general: authError });
    }
  }, [location, authError, loginUXV2, showSnackbar]);

  useEffect(() => {
    return () => { if (authError) clearError(); };
  }, [authError, clearError]);

  useEffect(() => {
    setErrors({});
    setUsername('');
    setPassword('');
  }, [userType]);

  const handleOAuthSuccess = async result => {
    try {
      if (result.token) localStorage.setItem('token', result.token);
      if (result.user) localStorage.setItem('user', JSON.stringify(result.user));
      localStorage.setItem('lastActivity', Date.now().toString());
      const defaultRedirect = userType === 'patient' ? '/patient/dashboard' : 
                             userType === 'asc' ? '/asc/dashboard' : '/dashboard';
      navigate(location.state?.from || defaultRedirect);
    } catch (error) {
      showSnackbar('Authentication completed but failed to complete login', 'error');
    }
  };

  const handleOAuthError = error => {
    const errorMsg = error.message || 'OAuth authentication failed';
    showSnackbar(errorMsg, 'error');
    setErrors({ general: errorMsg });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    setMessage('');

    let hasError = false;
    if (!username.trim()) {
      setErrors(prev => ({ ...prev, username: userType === 'patient' ? 'Email is required' : 'Username is required' }));
      hasError = true;
    } else if (userType === 'patient' && !/[^@\s]+@[^@\s]+\.[^@\s]+/.test(username)) {
      setErrors(prev => ({ ...prev, username: 'Enter a valid email address' }));
      hasError = true;
    }
    if (!password) {
      setErrors(prev => ({ ...prev, password: 'Password is required' }));
      hasError = true;
    }
    if (hasError) { setLoading(false); return; }

    try {
      const loginOptions = { 
        username, password,
        email: userType === 'patient' ? username : undefined,
        userType: userType === 'hospital' ? 'staff' : userType
      };
      const authOptions = { 
        provider: 'jwt',
        ascMode: userType === 'asc',
        hospitalMode: userType === 'hospital',
        rememberMe: true
      };
      
      const result = await login(loginOptions, authOptions);
      
      if (result.success) {
        showSnackbar('Login successful! Redirecting...', 'success');
        const redirectPath = result.redirectPath || location.state?.from || 
                           (userType === 'patient' ? '/patient/dashboard' : 
                            userType === 'asc' ? '/asc/dashboard' : '/dashboard');
        sessionStorage.setItem('loginRedirectHandled', 'true');
        navigate(redirectPath, { replace: true });
      } else {
        showSnackbar(result.error || 'Login failed', 'error');
        setErrors({ general: result.error || 'Login failed' });
      }
    } catch (err) {
      const config = getConfig();
      if (username.toLowerCase() === 'demo' || config.mode === 'demo') {
        showSnackbar(`${err.message} - Using DEMO mode as fallback.`, 'warning');
        const demoUser = { id: 'demo-user', username: 'demo', role: 'DEMO', name: 'Demo User' };
        localStorage.setItem('token', `demo-token-${Date.now()}`);
        localStorage.setItem('user', JSON.stringify(demoUser));
        localStorage.setItem('lastActivity', Date.now().toString());
        localStorage.setItem('demoMode', 'true');
        setEnvironmentMode('production');
        navigate('/dashboard');
      } else {
        let errorMessage = 'Login failed. Please try again.';
        if (err.message?.includes('Invalid') || err.message?.includes('credentials')) {
          errorMessage = userType === 'patient' 
            ? 'Invalid email or password.' : 'Invalid username or password.';
          setErrors({ username: 'Check credentials', password: 'Check credentials' });
        } else if (err.message?.includes('locked')) {
          errorMessage = 'Account locked. Contact support.';
        } else if (err.message && err.message !== '[object Object]') {
          errorMessage = err.message;
        }
        showSnackbar(errorMessage, 'error');
        setErrors({ general: errorMessage });
      }
    } finally {
      setLoading(false);
    }
  };

  const loginTabs = [
    { id: 'hospital', label: 'Hospital', icon: <LocalHospital /> },
    { id: 'asc', label: 'ASC', icon: <MedicalServices /> },
    { id: 'patient', label: 'Patient', icon: <PersonOutline /> },
  ];

  const getTitle = () => {
    switch (userType) {
      case 'patient': return 'Patient Portal';
      case 'asc': return 'ASC Portal';
      default: return 'Hospital Portal';
    }
  };

  const getSubtitle = () => {
    switch (userType) {
      case 'patient': return 'Access your medical records and appointments';
      case 'asc': return 'Ambulatory Surgery Center Management';
      default: return 'Healthcare Professional Access';
    }
  };

  const isLoading = loading || authLoading;
  const showEnterpriseAuthOptions = false;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, #1a237e 50%, ${theme.palette.primary.main} 100%)`,
        py: 4,
        px: 2,
      }}
    >
      <Fade in timeout={800}>
        <Paper
          elevation={24}
          sx={{
            width: '100%',
            maxWidth: 440,
            borderRadius: 4,
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          {/* Gradient Header */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              color: 'white',
              px: { xs: 2, sm: 3 },
              pt: { xs: 3, sm: 4 },
              pb: { xs: 2, sm: 3 },
              textAlign: 'center',
            }}
          >
            <VirtualPATLogo variant="full" size="medium" forceDark />
            
            <Typography variant="body2" sx={{ mt: { xs: 1.5, sm: 2 }, opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              Enterprise Clinical Platform
            </Typography>

            {/* Login Mode Tabs */}
            <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} sx={{ mt: { xs: 2, sm: 3 } }}>
              {loginTabs.map((tab) => (
                <LoginTab
                  key={tab.id}
                  icon={tab.icon}
                  label={tab.label}
                  active={userType === tab.id}
                  onClick={() => setUserType(tab.id)}
                />
              ))}
            </Stack>
          </Box>

          <AnimatePresence mode="wait">
            <motion.div
              key={userType}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <Box sx={{ p: { xs: 2, sm: 3 } }}>
                {userType && (
                  <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                    <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      {getTitle()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {getSubtitle()}
                    </Typography>
                  </Box>
                )}

                {!userType && (
                  <Box sx={{ py: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      Select a portal above to continue
                    </Typography>
                  </Box>
                )}

                {userType && (
                  <Box component="form" onSubmit={handleSubmit}>
                    {message && <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>{message}</Alert>}
                    {errors.general && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{errors.general}</Alert>}

                    <Stack spacing={2.5}>
                      <ModernTextField
                        theme={theme}
                        icon={userType === 'patient' ? <EmailIcon /> : <PersonIcon />}
                        label={userType === 'patient' ? 'Email Address' : 'Username'}
                        value={username}
                        onChange={(e) => {
                          setUsername(e.target.value);
                          setErrors(prev => ({ ...prev, username: '' }));
                        }}
                        error={!!errors.username}
                        helperText={errors.username}
                        disabled={isLoading}
                        autoComplete={userType === 'patient' ? 'email' : 'username'}
                        type={userType === 'patient' ? 'email' : 'text'}
                      />

                      <ModernTextField
                        theme={theme}
                        icon={<LockIcon />}
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setErrors(prev => ({ ...prev, password: '' }));
                        }}
                        error={!!errors.password}
                        helperText={errors.password}
                        disabled={isLoading}
                        autoComplete="current-password"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                size="small"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />

                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={isLoading}
                        sx={{
                          py: 1.5,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontSize: '1rem',
                          fontWeight: 700,
                          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                          boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
                          '&:hover': {
                            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                            boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                          },
                        }}
                      >
                        {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
                      </Button>
                    </Stack>

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mt: 2.5 }}
                    >
                      {userType === 'patient' ? (
                        <>
                          <Link
                            component="button"
                            type="button"
                            variant="body2"
                            onClick={() => navigate('/patient/forgot-password')}
                            sx={{ textDecoration: 'none', fontWeight: 500 }}
                          >
                            Forgot Password?
                          </Link>
                          <Link
                            component="button"
                            type="button"
                            variant="body2"
                            onClick={() => navigate('/patient/register')}
                            sx={{ textDecoration: 'none', fontWeight: 500 }}
                          >
                            Create Account
                          </Link>
                        </>
                      ) : (
                        <Link
                          component="button"
                          type="button"
                          variant="body2"
                          onClick={() => showSnackbar('Please contact your administrator', 'info')}
                          sx={{ textDecoration: 'none', fontWeight: 500 }}
                        >
                          Need Help?
                        </Link>
                      )}
                    </Stack>

                    {userType !== 'patient' && showEnterpriseAuthOptions && (
                      <>
                        <Divider sx={{ my: 3 }}><Typography variant="body2" color="text.secondary">OR</Typography></Divider>
                        <Stack spacing={2}>
                          <OAuthProviderList onSuccess={handleOAuthSuccess} onError={handleOAuthError} mode={userType} compact />
                          <SAMLLoginButton variant="outlined" fullWidth size="large" mode={userType} />
                        </Stack>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            </motion.div>
          </AnimatePresence>

          {/* Footer */}
          <Box
            sx={{
              px: 3,
              py: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.03),
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" color="text.secondary" align="center" display="block">
              © 2025 VirtualPAT. All rights reserved.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 0.5 }}>
              <Link href="/privacy" variant="caption" color="text.secondary" sx={{ textDecoration: 'none' }}>Privacy</Link>
              <Link href="/terms" variant="caption" color="text.secondary" sx={{ textDecoration: 'none' }}>Terms</Link>
            </Stack>
          </Box>
        </Paper>
      </Fade>
    </Box>
  );
}
